import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Slider,
  TextField,
  InputAdornment,
  Button,
  Divider,
} from '@mui/material';
import { brand, pageHeroRichSxFor, pageElevatedCardSx, pageHeroImages, pageHeroType } from '../brandTheme';

const EmiCalculator = () => {
  const [amount, setAmount] = useState(500000);
  const [interest, setInterest] = useState(10.5);
  const [tenure, setTenure] = useState(5);
  const [emi, setEmi] = useState(0);
  const [totalInterest, setTotalInterest] = useState(0);
  const [totalPayment, setTotalPayment] = useState(0);

  const calculateEmi = useCallback(() => {
    const principal = parseFloat(amount);
    const rate = parseFloat(interest) / 12 / 100;
    const time = parseFloat(tenure) * 12;

    if (principal && time) {
      let emiValue = 0;
      let totalAmt = 0;

      if (rate === 0) {
        emiValue = principal / time;
        totalAmt = principal;
      } else {
        emiValue =
          (principal * rate * Math.pow(1 + rate, time)) /
          (Math.pow(1 + rate, time) - 1);
        totalAmt = emiValue * time;
      }

      setEmi(Math.round(emiValue));
      setTotalPayment(Math.round(totalAmt));
      setTotalInterest(Math.round(totalAmt - principal));
    }
  }, [amount, interest, tenure]);

  useEffect(() => {
    calculateEmi();
  }, [calculateEmi]);

  const handleAmountChange = (event, newValue) => {
    setAmount(newValue);
  };

  const handleInterestChange = (event, newValue) => {
    setInterest(newValue);
  };

  const handleTenureChange = (event, newValue) => {
    setTenure(newValue);
  };

  const DonutChart = () => {
    const radius = 80;
    const stroke = 20;
    const normalizedRadius = radius - stroke * 0.5;
    const circumference = normalizedRadius * 2 * Math.PI;

    const principalPercent = totalPayment > 0 ? amount / totalPayment : 0;
    const principalOffset = circumference - principalPercent * circumference;

    return (
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 200,
        }}
      >
        <svg height={radius * 2} width={radius * 2} style={{ transform: 'rotate(-90deg)' }}>
          <circle stroke={brand.accent} fill="transparent" strokeWidth={stroke} r={normalizedRadius} cx={radius} cy={radius} />
          <circle
            stroke={brand.primary}
            fill="transparent"
            strokeWidth={stroke}
            strokeDasharray={`${circumference} ${circumference}`}
            style={{
              strokeDashoffset: principalOffset,
              transition: 'stroke-dashoffset 0.5s ease-in-out',
            }}
            r={normalizedRadius}
            cx={radius}
            cy={radius}
          />
        </svg>

        <Box sx={{ position: 'absolute', textAlign: 'center' }}>
          <Typography variant="caption" display="block" color="text.secondary">
            Monthly EMI
          </Typography>
          <Typography variant="h6" fontWeight={800} color="primary">
            ₹{emi.toLocaleString()}
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <Box id="emi-calculator" sx={{ bgcolor: brand.surfaceWarm, pb: { xs: 8, md: 10 } }} className="component-box component-box--flush">
      <Box sx={{ ...pageHeroRichSxFor(pageHeroImages.emiCalculator), textAlign: 'center' }}>
        <Container maxWidth="md">
          <Typography component="p" sx={{ ...pageHeroType.eyebrow, mb: 2.5, textAlign: 'center' }}>
            FINANCIAL TOOLS
          </Typography>
          <Typography component="h1" sx={{ ...pageHeroType.title, mb: 2.5, textAlign: 'center' }}>
            EMI <Box component="span" sx={{ color: '#fde68a' }}>Calculator</Box>
          </Typography>
          <Typography sx={{ ...pageHeroType.subtitle, mb: 4, mx: 'auto', textAlign: 'center' }}>
            Plan your loans smartly. Calculate your monthly installments and total interest payable in seconds with our easy-to-use calculator.
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => document.getElementById('calculator-section')?.scrollIntoView({ behavior: 'smooth' })}
            sx={{
              px: 4,
              py: 1.5,
              borderRadius: '999px',
              fontWeight: 800,
              bgcolor: '#fbbf24',
              color: '#422006',
              boxShadow: '0 8px 28px rgba(0,0,0,0.25)',
              '&:hover': { bgcolor: '#fcd34d' },
            }}
          >
            Calculate Now
          </Button>
        </Container>
      </Box>

      <Container id="calculator-section" maxWidth="lg" sx={{ mt: { xs: 4, md: 6 } }}>
        <Paper elevation={0} sx={{ ...pageElevatedCardSx, overflow: 'hidden' }}>
          <Grid container>
            <Grid item xs={12} md={7} sx={{ p: { xs: 3, md: 5 } }}>
              <Typography variant="h5" fontWeight={800} color="primary">
                Calculate Your EMI
              </Typography>

              <Box sx={{ mt: 4 }}>
                <Typography fontWeight={700}>Loan Amount ₹ {amount.toLocaleString()}</Typography>
                <Slider value={amount} min={100000} max={10000000} step={50000} onChange={handleAmountChange} />
                <TextField
                  value={amount}
                  onChange={(e) => setAmount(Number(e.target.value))}
                  fullWidth
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                  }}
                />
              </Box>

              <Box sx={{ mt: 4 }}>
                <Typography fontWeight={700}>Interest Rate {interest}%</Typography>
                <Slider value={interest} min={1} max={25} step={0.1} onChange={handleInterestChange} />
                <TextField
                  value={interest}
                  onChange={(e) => setInterest(Number(e.target.value))}
                  fullWidth
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                />
              </Box>

              <Box sx={{ mt: 4 }}>
                <Typography fontWeight={700}>Loan Tenure {tenure} Years</Typography>
                <Slider value={tenure} min={1} max={30} onChange={handleTenureChange} />
                <TextField
                  value={tenure}
                  onChange={(e) => setTenure(Number(e.target.value))}
                  fullWidth
                  InputProps={{
                    endAdornment: <InputAdornment position="end">Years</InputAdornment>,
                  }}
                />
              </Box>
            </Grid>

            <Grid
              item
              xs={12}
              md={5}
              sx={{
                p: { xs: 3, md: 5 },
                background: `linear-gradient(165deg, ${brand.surface} 0%, rgba(15, 118, 110, 0.08) 100%)`,
                borderLeft: { md: `1px solid ${brand.border}` },
                borderTop: { xs: `1px solid ${brand.border}`, md: 'none' },
              }}
            >
              <Typography variant="h6" fontWeight={800}>
                Repayment Details
              </Typography>
              <Divider sx={{ my: 3 }} />
              <DonutChart />
              <Typography sx={{ mt: 2 }}>Principal Amount: ₹ {amount.toLocaleString()}</Typography>
              <Typography>Total Interest: ₹ {totalInterest.toLocaleString()}</Typography>
              <Paper
                sx={{
                  mt: 3,
                  p: 3,
                  textAlign: 'center',
                  background: brand.ctaGradient,
                  color: 'white',
                  borderRadius: '16px',
                  border: 'none',
                }}
              >
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Total Amount Payable
                </Typography>
                <Typography variant="h5" fontWeight={800}>
                  ₹ {totalPayment.toLocaleString()}
                </Typography>
              </Paper>
              <Button variant="contained" fullWidth sx={{ mt: 3, borderRadius: '999px', py: 1.25, fontWeight: 800 }} href="#/contact">
                Apply for Loan
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Container>
    </Box>
  );
};

export default EmiCalculator;
